<template>
    <CampaignBanner
        class="library-banner"
        :class="$attrs.class"
        :master-template-height="displayDimensions.height"
        :master-template-width="displayDimensions.width"
        :max-height="maxHeight"
        :max-width="maxWidth"
        :scaling-factor="scalingFactor"
        :resize-mode="resizeMode"
        @toggleResizeMode="onToggleResizeMode"
        @updateInternalScalingFactor="internalScalingFactor = $event"
    >
        <template v-if="shouldShowPreflightHeader" #header>
            <Tooltip
                v-if="hasCreativeXEnabled && deliverable.preflight.status !== PreflightStatus.NOT_SENT"
                class="library-banner__header__tooltip"
                placement="right"
                transfer
                @click="openPreflightModal(PLATFORM.CREATIVEX)"
            >
                <div class="library-banner__header__left" :class="[scoreClass, statusClass]">
                    <Icon type="ios-information-circle-outline" />
                    <template v-if="preflightScore !== undefined">
                        <span class="library-banner__header__score-label">Creative X:</span>
                        <span class="library-banner__header__score"> {{ (preflightScore * 100).toFixed(0) }}% </span>
                    </template>
                </div>

                <template #content>
                    <p>Preflight status: {{ deliverable.preflight.status }}</p>
                    <p v-if="deliverable.preflight && 'score' in deliverable.preflight">
                        Score: {{ (deliverable.preflight.score * 100).toFixed(0) }}%
                    </p>
                    <p>Click for details.</p>
                </template>
            </Tooltip>

            <Tooltip
                v-if="hasSataliaEnabled && deliverable.preflight.status !== PreflightStatus.NOT_SENT"
                class="library-banner__header__tooltip satalia-tooltip"
                placement="right"
                transfer
                @click="openPreflightModal(PLATFORM.SATALIA)"
            >
                <div class="library-banner__header__left" :class="[sataliaStatusClass]">
                    <Icon type="ios-information-circle-outline" />
                    <span class="library-banner__header__score-label">Pre-flight:</span>
                    <span class="library-banner__header__score">{{ deliverable.preflight.status }}</span>
                </div>

                <template #content>
                    <p>Pre-flight status: {{ deliverable.preflight.status }}</p>
                    <p>Click for details.</p>
                </template>
            </Tooltip>
        </template>

        <template #bannerContainer>
            <Checkbox
                class="campaign-banner__checkbox"
                :label="deliverable.idHash"
                data-testid="campaign-banner-checkbox"
                @click.capture="onBannerToggleSelect"
            >
                <BannerPreview
                    :id="bannerId"
                    lazy-load
                    :preview-values="previewValues(deliverable)"
                    :ad-type="deliverable.masterTemplate.adType"
                    :scaling-factor="internalScalingFactor"
                    :playback-namespace="playbackNamespace"
                    @bannerLoaded="onBannerLoaded"
                />
            </Checkbox>
        </template>

        <template #bannerFooter>
            <BannerFooterStatus
                v-if="$auth.userCan($auth.Actions.CanChangeDeliverableQAStatus, { clientId, campaignId })"
                :deliverable="deliverable"
                @statusHasBeenUpdated="emitStatusHasBeenUpdated"
            />

            <BannerFooterStatusIndicator v-else :deliverable="deliverable" />

            <div class="library-banner__title">
                <BannerFooterInfoPoptip :deliverable="deliverable" />
            </div>

            <BannerFooterOptions
                :model-value="optionsVisible"
                :options="footerOptions"
                @update:modelValue="optionsVisible = $event"
            />
        </template>
    </CampaignBanner>

    <PreflightModal
        v-if="showPreflightModal"
        v-model="showPreflightModal"
        :deliverable-id="deliverable._id"
        :platform="preflightPlatform"
    />
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { PreflightStatus } from "hoxton-shared/enums/creativex";
import { PLATFORM } from "hoxton-shared/enums/exports";

import type { ConstructedDeliverable } from "@/types/types";
import { BannerEvents } from "@/enums/banners";

import PreflightModal from "@/components/Preflight/PreflightModal.vue";
import BannerPreview from "@/components/Previews/BannerPreview.vue";
import CampaignBanner from "@/components/Campaign/CampaignBanner.vue";
import BannerFooterInfoPoptip from "@/components/Previews/BannerFooter/BannerFooterInfoPoptip.vue";
import BannerFooterStatus from "@/components/Previews/BannerFooter/BannerFooterStatus.vue";
import BannerFooterStatusIndicator from "@/components/Previews/BannerFooter/BannerFooterStatusIndicator.vue";
import BannerFooterOptions from "@/components/Previews/BannerFooter/BannerFooterOptions.vue";

import { usePreviewValues } from "@/composables/usePreviewValues";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";
import { getBannerIdFromDeliverable } from "@/utils/getBannerIframeId";
import { getTemplateDisplayDimensions } from "@/utils";

export default defineComponent({
    components: {
        CampaignBanner,
        BannerPreview,
        BannerFooterInfoPoptip,
        BannerFooterStatusIndicator,
        BannerFooterStatus,
        BannerFooterOptions,
        PreflightModal
    },

    props: {
        deliverable: {
            type: Object as PropType<ConstructedDeliverable>,
            required: true
        },

        scalingFactor: {
            type: Number,
            required: true
        },

        maxHeight: {
            type: Number,
            required: true
        },

        maxWidth: {
            type: Number,
            required: true
        },

        playbackNamespace: {
            type: String,
            required: true
        }
    },

    emits: ["statusHasBeenUpdated", BannerEvents.BannerLoaded],

    setup() {
        const previewValues = usePreviewValues();
        const ga4 = useGA4Tracking();

        return {
            previewValues,
            ga4
        };
    },

    data() {
        return {
            showPreflightModal: false,
            preflightPlatform: PLATFORM.CREATIVEX as typeof PLATFORM.CREATIVEX | typeof PLATFORM.SATALIA,
            resizeMode: false,
            optionsVisible: false,
            internalScalingFactor: this.scalingFactor,
            PreflightStatus,
            PLATFORM
        };
    },

    computed: {
        preflightScore() {
            if (!this.deliverable?.preflight || typeof this.deliverable.preflight !== "object") {
                return undefined;
            }
            if (!("score" in this.deliverable.preflight)) {
                return undefined;
            }

            return this.deliverable.preflight.score;
        },

        scoreClass() {
            if (!this.deliverable?.preflight || typeof this.deliverable.preflight !== "object") {
                return "";
            }
            if (!("score" in this.deliverable.preflight)) {
                return "";
            }

            if (this.deliverable.preflight.score >= 0.8) {
                return "score_80";
            } else if (this.deliverable.preflight.score >= 0.4) {
                return "score_40";
            } else {
                return "score_0";
            }
        },

        statusClass() {
            if (!this.deliverable?.preflight) {
                return "";
            }
            switch (this.deliverable.preflight.status) {
                case PreflightStatus.NOT_SENT:
                    return "";
                case PreflightStatus.MODIFIED:
                    return "status_modified";
                case PreflightStatus.PROCESSING:
                    return "status_pending";
                case PreflightStatus.ERROR:
                    return "status_error";
                case PreflightStatus.COMPLETED:
                    return "";
                default:
                    return "";
            }
        },

        footerOptions() {
            const options = [
                {
                    id: "show-drawer",
                    label: "Review",
                    onClick: this.showDrawer
                }
            ];

            return options;
        },

        bannerId() {
            return getBannerIdFromDeliverable(this.deliverable, this.playbackNamespace);
        },

        displayDimensions() {
            return getTemplateDisplayDimensions(this.deliverable.masterTemplate);
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasCreativeXEnabled() {
            return this.$store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;
        },

        hasSataliaEnabled() {
            return this.$store.state.ui.currentClient?.sataliaPlatformSettings?.enabled ?? false;
        },

        shouldShowPreflightHeader() {
            const hasCreativeX =
                this.hasCreativeXEnabled && this.deliverable.preflight.status !== PreflightStatus.NOT_SENT;
            const hasSatalia = this.hasSataliaEnabled && this.deliverable.preflight.status !== PreflightStatus.NOT_SENT;
            return hasCreativeX || hasSatalia;
        },

        sataliaStatusClass() {
            if (!this.deliverable?.preflight) {
                return "";
            }

            switch (this.deliverable.preflight.status) {
                case PreflightStatus.NOT_SENT:
                    return "";
                case PreflightStatus.MODIFIED:
                    return "status_modified";
                case PreflightStatus.PROCESSING:
                    return "status_pending";
                case PreflightStatus.ERROR:
                    return "status_error";
                case PreflightStatus.COMPLETED:
                    return "";
                default:
                    return "";
            }
        }
    },

    methods: {
        emitStatusHasBeenUpdated() {
            this.$emit("statusHasBeenUpdated");
        },

        onBannerLoaded() {
            this.$emit(BannerEvents.BannerLoaded);
        },

        showDrawer() {
            this.onOptionsHide();

            this.$store.dispatch(DeliverableLibraryAction.SetDrawerIsVisible, true);
            this.$store.dispatch(DeliverableLibraryAction.SetDrawerDeliverables, [this.deliverable]);
        },

        onOptionsHide() {
            this.optionsVisible = false;
        },

        onToggleResizeMode() {
            this.resizeMode = !this.resizeMode;
            this.onOptionsHide();
        },

        openPreflightModal(platform: typeof PLATFORM.CREATIVEX | typeof PLATFORM.SATALIA) {
            this.preflightPlatform = platform;
            this.showPreflightModal = true;
        },

        onBannerToggleSelect(event: MouseEvent) {
            const target = event.target as HTMLElement;

            if (target.tagName === "INPUT") {
                this.ga4.trackClickEvent({
                    interactionObject: GA4.CLICK_INTERACTION_OBJECTS.CHECKBOX,
                    interactionText: `${GA4.CLICK_INTERACTION_TEXTS.TOGGLE_DELIVERABLE} ${this.bannerId}`
                });
            }
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.library-banner__title {
    text-align: start;
    color: $darktheme20;
    flex: 1 0;
    font-size: $font-size-small;
    min-width: 0;
}

.library-banner__header__tooltip {
    flex-grow: 1;
    container-type: inline-size;
}

.library-banner__header__left {
    height: 40px;

    padding: 0 8px;

    display: flex;
    align-items: center;
    gap: 8px;

    cursor: pointer;

    &:hover {
        background-color: var(--wpp-grey-color-300);
    }
}

@container (width < 50px) {
    .library-banner__header__score {
        display: none;
    }
}

@container (width < 150px) {
    .library-banner__header__score-label {
        display: none;
    }
}

.score_80 {
    background-color: var(--wpp-success-color-100);
    color: var(--wpp-success-color-600);

    &:hover {
        background-color: var(--wpp-success-color-200);
    }
}

.score_40,
.status_modified {
    background-color: var(--wpp-warning-color-100);
    color: var(--wpp-warning-color-600);

    &:hover {
        background-color: var(--wpp-warning-color-200);
    }
}

.score_0,
.status_error {
    background-color: var(--wpp-danger-color-200);
    color: var(--wpp-danger-color-600);

    &:hover {
        background-color: var(--wpp-danger-color-200);
    }
}

.status_pending {
    background-color: var(--wpp-blue-color-100);
    color: var(--wpp-blue-color-600);

    &:hover {
        background-color: var(--wpp-blue-color-200);
    }
}

body:has(> .wpp-theme-dark) {
    .score_80 {
        background-color: var(--wpp-success-color-900);
        color: var(--wpp-success-color-400);

        &:hover {
            background-color: var(--wpp-success-color-800);
        }
    }

    .score_40,
    .status_modified {
        background-color: var(--wpp-warning-color-900);
        color: var(--wpp-warning-color-400);

        &:hover {
            background-color: var(--wpp-warning-color-800);
        }
    }

    .score_0,
    .status_error {
        background-color: var(--wpp-danger-color-900);
        color: var(--wpp-danger-color-400);

        &:hover {
            background-color: var(--wpp-danger-color-800);
        }
    }

    .status_pending {
        background-color: var(--wpp-blue-color-900);
        color: var(--wpp-blue-color-400);

        &:hover {
            background-color: var(--wpp-blue-color-800);
        }
    }
}
</style>
